import { Component, signal, inject, output } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-password-change-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" (click)="onBackdropClick($event)">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4" (click)="$event.stopPropagation()">
        <div class="p-6">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Change Your Password
          </h2>
          
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            This is your first login. Please create a new password to continue.
          </p>

          <form [formGroup]="passwordForm" (ngSubmit)="onSubmit()">
            <!-- Current Password (only if not first-time change) -->
            <div *ngIf="!isFirstTime" class="mb-4">
              <label for="currentPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                type="password"
                formControlName="currentPassword"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                [class.border-red-500]="passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched"
              />
              <p *ngIf="passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched" 
                 class="mt-1 text-sm text-red-600 dark:text-red-400">
                Current password is required
              </p>
            </div>

            <!-- New Password -->
            <div class="mb-4">
              <label for="newPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                formControlName="newPassword"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                [class.border-red-500]="passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched"
                [class.border-green-500]="isPasswordStrong() && passwordForm.get('newPassword')?.value"
              />
              
              <!-- Password Requirements -->
              <div class="mt-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Password must contain:</p>
                <ul class="space-y-1 text-xs">
                  <li class="flex items-center" [class.text-green-600]="getPasswordRequirements().minLength" [class.text-gray-500]="!getPasswordRequirements().minLength">
                    <span [class.text-green-500]="getPasswordRequirements().minLength" [class.text-gray-400]="!getPasswordRequirements().minLength" class="mr-2">
                      <ng-container *ngIf="getPasswordRequirements().minLength">✓</ng-container>
                      <ng-container *ngIf="!getPasswordRequirements().minLength">○</ng-container>
                    </span>
                    At least 8 characters
                  </li>
                  <li class="flex items-center" [class.text-green-600]="getPasswordRequirements().lowercase" [class.text-gray-500]="!getPasswordRequirements().lowercase">
                    <span [class.text-green-500]="getPasswordRequirements().lowercase" [class.text-gray-400]="!getPasswordRequirements().lowercase" class="mr-2">
                      <ng-container *ngIf="getPasswordRequirements().lowercase">✓</ng-container>
                      <ng-container *ngIf="!getPasswordRequirements().lowercase">○</ng-container>
                    </span>
                    One lowercase letter (a-z)
                  </li>
                  <li class="flex items-center" [class.text-green-600]="getPasswordRequirements().uppercase" [class.text-gray-500]="!getPasswordRequirements().uppercase">
                    <span [class.text-green-500]="getPasswordRequirements().uppercase" [class.text-gray-400]="!getPasswordRequirements().uppercase" class="mr-2">
                      <ng-container *ngIf="getPasswordRequirements().uppercase">✓</ng-container>
                      <ng-container *ngIf="!getPasswordRequirements().uppercase">○</ng-container>
                    </span>
                    One uppercase letter (A-Z)
                  </li>
                  <li class="flex items-center" [class.text-green-600]="getPasswordRequirements().number" [class.text-gray-500]="!getPasswordRequirements().number">
                    <span [class.text-green-500]="getPasswordRequirements().number" [class.text-gray-400]="!getPasswordRequirements().number" class="mr-2">
                      <ng-container *ngIf="getPasswordRequirements().number">✓</ng-container>
                      <ng-container *ngIf="!getPasswordRequirements().number">○</ng-container>
                    </span>
                    One number (0-9)
                  </li>
                  <li class="flex items-center" [class.text-green-600]="getPasswordRequirements().specialChar" [class.text-gray-500]="!getPasswordRequirements().specialChar">
                    <span [class.text-green-500]="getPasswordRequirements().specialChar" [class.text-gray-400]="!getPasswordRequirements().specialChar" class="mr-2">
                      <ng-container *ngIf="getPasswordRequirements().specialChar">✓</ng-container>
                      <ng-container *ngIf="!getPasswordRequirements().specialChar">○</ng-container>
                    </span>
                    One special character (!&#64;#$%^&amp;*...)
                  </li>
                </ul>
              </div>
            </div>

            <!-- Confirm Password -->
            <div class="mb-6">
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                formControlName="confirmPassword"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                [class.border-red-500]="passwordForm.get('confirmPassword')?.invalid && passwordForm.get('confirmPassword')?.touched"
              />
              <p *ngIf="passwordForm.get('confirmPassword')?.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched" 
                 class="mt-1 text-sm text-red-600 dark:text-red-400">
                Passwords do not match
              </p>
            </div>

            <!-- Error Message -->
            <div *ngIf="errorMessage()" class="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
              {{ errorMessage() }}
            </div>

            <!-- Buttons -->
            <div class="flex justify-end space-x-3">
              <button
                type="button"
                (click)="onCancel()"
                [disabled]="loading()"
                class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                [disabled]="passwordForm.invalid || !isPasswordStrong() || loading()"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span *ngIf="!loading()">Change Password</span>
                <span *ngIf="loading()">Changing...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
})
export class PasswordChangeModalComponent {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  
  isFirstTime = true; // For first-time users, current password is not required
  passwordForm: FormGroup;
  loading = signal(false);
  errorMessage = signal<string>('');
  
  passwordChanged = output<void>();

  constructor() {
    // For first-time password change, current password is not required
    this.passwordForm = this.fb.group({
      currentPassword: [''], // Optional for first-time
      newPassword: ['', [Validators.required, this.strongPasswordValidator()]],
      confirmPassword: ['', [Validators.required]],
    }, { validators: this.passwordMatchValidator });
  }

  strongPasswordValidator() {
    return (control: any) => {
      if (!control.value) {
        return null;
      }
      const password = control.value;
      const errors: any = {};

      if (password.length < 8) {
        errors.minLength = true;
      }
      if (!/[a-z]/.test(password)) {
        errors.lowercase = true;
      }
      if (!/[A-Z]/.test(password)) {
        errors.uppercase = true;
      }
      if (!/[0-9]/.test(password)) {
        errors.number = true;
      }
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors.specialChar = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  getPasswordRequirements() {
    const password = this.passwordForm.get('newPassword')?.value || '';
    return {
      minLength: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
  }

  isPasswordStrong(): boolean {
    const reqs = this.getPasswordRequirements();
    return reqs.minLength && reqs.lowercase && reqs.uppercase && reqs.number && reqs.specialChar;
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (confirmPassword?.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }
    
    return null;
  }

  onSubmit() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    const currentPassword = this.isFirstTime ? null : this.passwordForm.value.currentPassword;
    const newPassword = this.passwordForm.value.newPassword;

    this.auth.changePassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.loading.set(false);
        this.passwordChanged.emit();
      },
      error: (err) => {
        this.loading.set(false);
        this.errorMessage.set(err.error?.message || 'Failed to change password. Please try again.');
      }
    });
  }

  onCancel() {
    // Cannot cancel on first-time password change - user must change password
    if (this.isFirstTime) {
      this.errorMessage.set('You must change your password to continue.');
      return;
    }
    this.passwordChanged.emit(); // For non-first-time, allow cancel
  }

  onBackdropClick(event: MouseEvent) {
    // Prevent closing on backdrop click for first-time password change
    if (this.isFirstTime) {
      event.preventDefault();
      return;
    }
    // For non-first-time, allow closing
  }
}

